// src/context/UserContext.tsx
import { notify } from "@/components/common/Notification";
import {
  clearLocalStorage,
  getAccessToken,
  getRefreshToken,
  getTenantId,
  setLocalAccessToken,
  setLocalRefreshToken,
  setLocalTenantId,
  setLocalUserId,
  setLocalUserInfo,
} from "@/lib/storeManager";
import { AuthService, UserPreferencesService } from "@/services/APIs";
import { axiosConfig } from "@/services/axiosConfig";
import { AuthRes, SocialLoginRes } from "@/utils/types/auth";
import { REFRESH_TOKEN_INTERVAL_MS } from "@/utils/constant/authTokens";
import { UserPreferencesData } from "@/utils/types/userPreferencesGrid";
import { jwtDecode } from "jwt-decode";
import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from "react";

interface DecodedToken {
  userId: string;
  email: string;
  exp: number;
  [key: string]: unknown;
}

interface UserContextType {
  jwtToken: string | null;
  refreshToken: string | null;
  decodedToken: DecodedToken | null;
  isAuthenticated: boolean;
  isBootstrapping: boolean;
  preferencesData: UserPreferencesData | null;
  fetchPreferencesData: () => Promise<void>;
  signup: (
    email: string,
    password: string,
    first_name: string,
    last_name: string
  ) => Promise<AuthRes | undefined>;
  login: (email: string, password: string) => Promise<AuthRes | undefined>;
  logout: (options?: { reload?: boolean }) => Promise<void>;
  socialLogin: (provider: string) => Promise<SocialLoginRes | undefined>;
  forgotPassword: (email: string) => Promise<{ message?: string } | undefined>;
  forgotPasswordReset: (
    id: string,
    payload: {
      new_password: string;
      confirm_password: string;
    }
  ) => Promise<{ message?: string } | undefined>;
  resetPassword: (values: {
    current_password: string;
    new_password: string;
  }) => Promise<{ message?: string } | undefined>;
  /** Sync JWT + user info in memory after member `/auth/` registration (no separate login call). */
  applyAuthSession: (payload: {
    access: string;
    refresh: string;
    user: {
      id?: string;
      email: string;
      first_name?: string;
      last_name?: string;
      full_name?: string;
      user_type?: string;
    };
  }) => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  // Local state
  const [jwtToken, setJwtToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [decodedToken, setDecodedToken] = useState<DecodedToken | null>(null);
  const [preferencesData, setPreferencesData] =
    useState<UserPreferencesData | null>(null);
  const [isBootstrapping, setIsBootstrapping] = useState<boolean>(true);

  // Bootstrap from localStorage
  useEffect(() => {
    const access = getAccessToken();
    const refresh = getRefreshToken();
    if (access && refresh) {
      try {
        const decoded = jwtDecode<DecodedToken>(access);
        setJwtToken(access);
        setRefreshToken(refresh);
        setDecodedToken(decoded);
      } catch {
        const preservedTenant = getTenantId();
        clearLocalStorage();
        if (preservedTenant !== null) {
          setLocalTenantId(preservedTenant);
        }
      }
    }
    setIsBootstrapping(false);
  }, []);

  const isAuthenticated =
    Boolean(jwtToken && decodedToken) && decodedToken!.exp * 1000 > Date.now();

  // Fetch preferences data function
  const fetchPreferencesData = useCallback(async () => {
    const data = await UserPreferencesService.getUserPreferencesGrid();
    if (data) {
      setPreferencesData(data);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchPreferencesData();
    }
  }, [isAuthenticated, fetchPreferencesData]);

  const applyAuthSession = useCallback(
    (payload: {
      access: string;
      refresh: string;
      user: {
        id?: string;
        email: string;
        first_name?: string;
        last_name?: string;
        full_name?: string;
        user_type?: string;
      };
    }) => {
      const { access, refresh, user } = payload;
      if (!access?.trim() || !refresh?.trim()) {
        return;
      }
      setJwtToken(access);
      setDecodedToken(jwtDecode<DecodedToken>(access));
      setLocalAccessToken(access);
      axiosConfig.defaults.headers.common.Authorization = `Bearer ${access}`;
      setRefreshToken(refresh);
      setLocalRefreshToken(refresh);
      const full = (user.full_name ?? "").trim();
      const parts = full.split(/\s+/).filter(Boolean);
      const firstFromFull = parts[0] ?? "";
      const lastFromFull = parts.slice(1).join(" ");
      setLocalUserInfo({
        first_name: (user.first_name ?? firstFromFull).trim() || firstFromFull,
        last_name: (user.last_name ?? lastFromFull).trim() || lastFromFull,
        email: user.email,
        user_type: user.user_type ?? "member",
      });
      if (user.id?.trim()) {
        setLocalUserId(user.id.trim());
      }
    },
    []
  );

  const handleSignup = async (
    userEmail: string,
    userPassword: string,
    userFirstName: string,
    userLastName: string
  ) => {
    const response = await AuthService.signup({
      email: userEmail,
      password: userPassword,
      first_name: userFirstName,
      last_name: userLastName,
    });
    type TokenPayload = { access: string; refresh: string };
    const payload = (response?.data ?? response) as TokenPayload;
    const access = payload?.access;
    const refresh = payload?.refresh;

    if (response) {
      setJwtToken(access);
      const decoded = jwtDecode<DecodedToken>(access);
      setDecodedToken(decoded);
      setLocalAccessToken(access);
      if (access) {
        axiosConfig.defaults.headers.common.Authorization = `Bearer ${access}`;
      }
      setRefreshToken(refresh);
      setLocalRefreshToken(refresh);
      const {
        first_name,
        last_name,
        email: responseEmail,
        user_type,
      } = response.user;
      setLocalUserInfo({
        first_name,
        last_name,
        email: responseEmail,
        user_type,
      });
      return response;
    }
  };

  const handleLogin = async (userEmail: string, userPassword: string) => {
    const response = await AuthService.login({
      email: userEmail,
      password: userPassword,
    });
    if (response) {
      setJwtToken(response.access);
      const decoded = jwtDecode<DecodedToken>(response.access);
      setDecodedToken(decoded);
      setLocalAccessToken(response.access);
      axiosConfig.defaults.headers.common.Authorization = `Bearer ${response.access}`;
      setRefreshToken(response.refresh);
      setLocalRefreshToken(response.refresh);
      const {
        first_name,
        last_name,
        email: responseEmail,
        user_type,
      } = response.user;
      setLocalUserInfo({
        first_name,
        last_name,
        email: responseEmail,
        user_type,
      });
      return response;
    }
  };

  const handleForgotPassword = async (userEmail: string) => {
    const response = await AuthService.forgotPassword(userEmail);
    if (response) {
      notify.success("Reset password email sent successfully");
      return response;
    }
  };

  const handleForgotPasswordReset = async (
    id: string,
    payload: {
      new_password: string;
      confirm_password: string;
    }
  ) => {
    const response = await AuthService.forgotPasswordReset(id, payload);
    if (response) {
      notify.success("Password reset successfully");
      return response;
    }
  };

  const handleLogout = useCallback(async () => {
    let response;

    try {
      response = await AuthService.logout();
    } finally {
      setJwtToken(null);
      setRefreshToken(null);
      setDecodedToken(null);
      clearLocalStorage();
      delete axiosConfig.defaults.headers.common.Authorization;
    }

    return response;
  }, []);

  const refreshSessionTokens = useCallback(async () => {
    try {
      const response = await AuthService.refreshToken();
      const access = response.access?.trim();
      if (!access) {
        return;
      }
      setJwtToken(access);
      setDecodedToken(jwtDecode<DecodedToken>(access));
      setLocalAccessToken(access);
      axiosConfig.defaults.headers.common.Authorization = `Bearer ${access}`;
      const nextRefresh = response.refresh?.trim();
      if (nextRefresh) {
        setRefreshToken(nextRefresh);
        setLocalRefreshToken(nextRefresh);
      }
    } catch {
      await handleLogout();
    }
  }, [handleLogout]);

  useEffect(() => {
    if (!jwtToken || !refreshToken) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      void refreshSessionTokens();
    }, REFRESH_TOKEN_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [jwtToken, refreshToken, refreshSessionTokens]);

  const handleResetPassword = async (values: {
    current_password: string;
    new_password: string;
  }) => {
    const response = await AuthService.resetPassword(values);
    return response;
  };

  const handleSocialLogin = async (provider: string) => {
    const response = await AuthService.socialLogin(provider);
    return response;
  };

  return (
    <UserContext.Provider
      value={{
        jwtToken,
        refreshToken,
        decodedToken,
        isAuthenticated,
        isBootstrapping,
        preferencesData,
        fetchPreferencesData,
        signup: handleSignup,
        login: handleLogin,
        logout: handleLogout,
        socialLogin: handleSocialLogin,
        forgotPassword: handleForgotPassword,
        forgotPasswordReset: handleForgotPasswordReset,
        resetPassword: handleResetPassword,
        applyAuthSession,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

// Export the context for use in other files
export { UserContext };
